/**
 * Client-only "recently viewed" rail, rendered by snippets/recently-viewed.liquid
 * (used by both sections/footer.liquid's built-in band and the standalone
 * sections/recently-viewed.liquid). Runs once per [data-recently-viewed]
 * root on the page, so more than one instance can coexist. The list of
 * viewed product handles lives in localStorage only - there is no
 * server-side record - so this hydrates entirely client side via
 * Shopify's `/products/<handle>.js` endpoint, mirroring
 * assets/wishlist-page.js's approach for the wishlist page.
 *
 * Recording: `[data-recently-viewed]` carries `data-current-handle` on
 * product pages (set by sections/product.liquid); on load that handle moves
 * to the front of the stored list, capped at 8, and is excluded from the
 * rendered rail on that same page (a product doesn't need to recommend
 * itself). Handles that 404 (deleted/unpublished) are pruned from storage;
 * handles that fetch fine but are no longer available are dropped from the
 * render only, so a restock can bring them back without the shopper having
 * re-viewed the product.
 *
 * The band stays out of the DOM's visible flow (`hidden`) until at least 2
 * valid products remain - this is a bonus recall rail, not primary content,
 * so a cold visit or a rail that resolves to 0-1 items shows nothing rather
 * than a skeleton or an empty state.
 */
const RENDER_LIMIT = 5;

(function () {
  const STORAGE_KEY = 'vellum:recentlyViewed';
  const MAX = 8;

  function read() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (error) {
      return [];
    }
  }

  function write(handles) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  }

  function record(handle) {
    if (!handle) return;
    const handles = read().filter((existing) => existing !== handle);
    handles.unshift(handle);
    write(handles.slice(0, MAX));
  }

  function clear() {
    window.localStorage.removeItem(STORAGE_KEY);
  }

  window.VellumRecentlyViewed = { read, record, clear };
})();

function formatMoney(cents) {
  return `Rs. ${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function buildRecentlyViewedCard(template, product) {
  const card = template.content.firstElementChild.cloneNode(true);
  const variant = product.variants.find((entry) => entry.available) || product.variants[0];

  card.querySelectorAll('[data-recently-viewed-card-link]').forEach((link) => {
    link.href = `/products/${product.handle}`;
  });

  const image = card.querySelector('[data-recently-viewed-card-image]');
  if (image) {
    image.src = product.featured_image || product.images?.[0] || '';
    image.alt = product.title;
  }

  const titleLink = card.querySelector('[data-recently-viewed-card-title-link]');
  if (titleLink) titleLink.textContent = product.title;

  const price = card.querySelector('[data-recently-viewed-card-price]');
  if (price) price.textContent = variant ? formatMoney(variant.price) : '';

  return card;
}

async function loadRecentlyViewedRail(root) {
  const currentHandle = root.dataset.currentHandle || null;
  if (currentHandle) window.VellumRecentlyViewed.record(currentHandle);

  const storedHandles = window.VellumRecentlyViewed.read().filter((handle) => handle !== currentHandle);
  if (storedHandles.length === 0) return;

  const results = await Promise.allSettled(
    storedHandles.map((handle) =>
      fetch(`/products/${handle}.js`).then((response) => {
        if (!response.ok) throw new Error(`${response.status}`);
        return response.json();
      })
    )
  );

  const survivingHandles = [];
  const availableProducts = [];

  results.forEach((result, index) => {
    if (result.status !== 'fulfilled') return;
    survivingHandles.push(storedHandles[index]);
    if (result.value.available) availableProducts.push(result.value);
  });

  if (survivingHandles.length !== storedHandles.length) {
    const prunedHandles = currentHandle
      ? [currentHandle, ...survivingHandles].filter((handle, index, all) => all.indexOf(handle) === index)
      : survivingHandles;
    window.localStorage.setItem('vellum:recentlyViewed', JSON.stringify(prunedHandles));
  }

  if (availableProducts.length < 2) return;

  const grid = root.querySelector('[data-recently-viewed-grid]');
  const template = document.querySelector('[data-recently-viewed-card-template]');
  if (!grid || !template) return;

  grid.innerHTML = '';
  availableProducts.slice(0, RENDER_LIMIT).forEach((product) => {
    grid.append(buildRecentlyViewedCard(template, product));
  });

  root.hidden = false;
}

document.addEventListener('click', (event) => {
  const clearButton = event.target.closest('[data-recently-viewed-clear]');
  if (!clearButton) return;

  window.VellumRecentlyViewed.clear();
  clearButton.closest('[data-recently-viewed]').hidden = true;
});

function initRecentlyViewed() {
  document.querySelectorAll('[data-recently-viewed]').forEach((root) => {
    loadRecentlyViewedRail(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecentlyViewed);
} else {
  initRecentlyViewed();
}
